import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { SectionHeading } from "@/components/bespoke/section-heading";
import { useState } from "react";
import { LoadingState } from "@/components/states";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  ApiError,
  instructionsHistoryQuery,
  instructionsQuery,
  restoreInstructions,
  saveInstructions,
} from "@/lib/api";
import { formatDate } from "@/lib/format";
import { SettingsFailure } from "./settings-feedback";

/**
 * The Instructions editor inside Project settings Artifacts: the current version in a
 * textarea, the version line beneath it, and the history with a restore action per version.
 * Every save creates a new version and is audited; restores append too, never overwrite.
 */
export function InstructionsSection({
  projectId,
  canEdit,
}: {
  projectId: string;
  canEdit: boolean;
}) {
  const queryClient = useQueryClient();
  const current = useQuery({ ...instructionsQuery(projectId), retry: false });
  const history = useQuery({ ...instructionsHistoryQuery(projectId), retry: false });
  const [draft, setDraft] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const currentKey = instructionsQuery(projectId).queryKey;
  const historyKey = instructionsHistoryQuery(projectId).queryKey;

  const save = useMutation({
    mutationFn: (body: string) => saveInstructions(projectId, { body }),
    onSuccess: (value) => {
      queryClient.setQueryData(currentKey, value);
      queryClient.setQueryData(historyKey, [
        value,
        ...(history.data ?? []).filter((row) => row.id !== value.id),
      ]);
      setDraft(null);
      setNotice(`Version ${value.version} saved.`);
    },
  });

  const restore = useMutation({
    mutationFn: (version: number) => restoreInstructions(projectId, version),
    onSuccess: (value) => {
      queryClient.setQueryData(currentKey, value);
      queryClient.setQueryData(historyKey, [value, ...(history.data ?? [])]);
      setDraft(null);
      setNotice(`Version ${value.version} saved.`);
    },
  });

  if (current.isPending) {
    return (
      <>
        <SectionHeading>Instructions</SectionHeading>
        <LoadingState label="Loading instructions" rows={4} />
      </>
    );
  }

  const missing =
    current.isError && current.error instanceof ApiError && current.error.status === 404;
  if (current.isError && !missing) {
    return (
      <>
        <SectionHeading>Instructions</SectionHeading>
        <SettingsFailure error={current.error} retry={() => void current.refetch()} />
      </>
    );
  }

  const versions = history.data ?? [];
  const shown = draft ?? current.data?.body ?? "";
  const dirty = draft !== null && draft !== (current.data?.body ?? "");

  return (
    <div className="flex flex-col gap-3">
      <div>
        <SectionHeading>Instructions</SectionHeading>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Rules every agent follows for this project. Every save creates a new version and is
          audited.
        </p>
      </div>
      {notice ? (
        <p role="status" className="text-sm text-muted-foreground">
          {notice}
        </p>
      ) : null}
      {save.error ? <SettingsFailure error={save.error} /> : null}
      {restore.error ? <SettingsFailure error={restore.error} /> : null}
      <Card>
        <div className="flex flex-col gap-3 p-5">
          <label
            className="flex flex-col gap-1.5 text-sm font-medium"
            htmlFor="project-instructions"
          >
            Current instructions
          </label>
          <textarea
            id="project-instructions"
            rows={9}
            disabled={!canEdit || save.isPending}
            value={shown}
            onChange={(event) => {
              setDraft(event.target.value);
              setNotice(null);
            }}
            placeholder={missing ? "Use TypeScript strict mode." : undefined}
            className="min-h-36 w-full rounded-md border border-input bg-card px-3 py-2 font-mono text-xs text-foreground outline-none focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ring disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground"
          />
          <div className="flex flex-wrap items-center gap-3">
            {canEdit ? (
              <Button
                type="button"
                size="sm"
                disabled={!dirty || save.isPending || shown.trim().length === 0}
                onClick={() => save.mutate(shown)}
              >
                {save.isPending ? "Saving…" : missing ? "Save instructions" : "Save new version"}
              </Button>
            ) : null}
            <p className="text-xs text-muted-foreground">
              {current.data
                ? `Version ${current.data.version} · edited by ${current.data.authorName ?? "a project member"} on ${formatDate(current.data.createdAt)}`
                : "No instructions saved yet."}
            </p>
          </div>
        </div>
      </Card>
      <Card>
        <div className="border-b px-5 py-3">
          <h3 className="text-sm font-medium">Version history</h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Restoring a version saves its text as a new version. History is never edited.
          </p>
        </div>
        {history.isPending ? (
          <div className="px-5 py-3">
            <LoadingState label="Loading versions" rows={2} />
          </div>
        ) : history.isError ? (
          <div className="px-5 py-3">
            <SettingsFailure error={history.error} retry={() => void history.refetch()} />
          </div>
        ) : versions.length === 0 ? (
          <p className="px-5 py-3 text-sm text-muted-foreground">No versions yet.</p>
        ) : (
          <ul className="divide-y">
            {versions.map((version) => (
              <li
                key={version.id}
                className="flex flex-wrap items-center justify-between gap-2 px-5 py-2.5 text-sm"
              >
                <span className="min-w-0">
                  <span className="block font-medium">Version {version.version}</span>
                  <span className="block truncate text-xs text-muted-foreground tabular-nums">
                    {version.authorName ?? "A project member"} · {formatDate(version.createdAt)}
                  </span>
                </span>
                {canEdit && current.data && version.version !== current.data.version ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={restore.isPending}
                    onClick={() => restore.mutate(version.version)}
                  >
                    {restore.isPending ? "Restoring…" : `Restore version ${version.version}`}
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
